import React, { useState, useRef, useEffect } from 'react';
import { 
  Typography, 
  Box, 
  Paper, 
  TextField, 
  Button, 
  List, 
  ListItem, 
  Avatar, 
  CircularProgress,
  Divider,
  IconButton,
  Card,
  CardContent,
  Link,
  Chip,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material';
import { 
  Send as SendIcon,
  SmartToy as BotIcon,
  Person as PersonIcon,
  Delete as DeleteIcon,
  Save as SaveIcon,
  Link as LinkIcon,
  Info as InfoIcon,
  Close as CloseIcon
} from '@mui/icons-material';
import { ChatMessage, ChatSource, ChatResponse } from '../types';
import { sendChatMessage, clearChatHistory } from '../services/api';

const DataQA: React.FC = () => {
  const [messages, setMessages] = useState<ChatMessage[]>([
    {
      id: '1',
      role: 'assistant',
      content: 'Hello! I\'m OpenAna, your data assistant. You can ask me questions about your data from Salesforce, Affinity, or your data warehouse. How can I help you today?',
      timestamp: new Date().toISOString()
    }
  ]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [sources, setSources] = useState<ChatSource[]>([]);
  const [selectedSource, setSelectedSource] = useState<ChatSource | null>(null);
  const [sourceDialogOpen, setSourceDialogOpen] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const chatMessages = useRef<HTMLDivElement>(null);

  // Sample questions for the user to try
  const sampleQuestions = [
    "What are the top 5 accounts by revenue in SFDC?",
    "Give me count of companies by region",
    "Can you give me the top performing accounts by region?",
    "Summarize the key differences in data models between systems"
  ];

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setInput(e.target.value);
  };

  // Function to display sources
  const displaySources = (sources: ChatSource[]) => {
    setSources(sources);
  };

  // Function to send a message to the API
  async function sendMessage(message: string) {
    // Show loading message
    const loadingDiv = document.createElement('div');
    loadingDiv.className = 'loading';
    loadingDiv.textContent = 'Thinking...';
    chatMessages.current?.appendChild(loadingDiv);
    
    try {
      const response = await fetch('/api/chat', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ message })
      });
      
      // Remove loading message
      if (chatMessages.current?.contains(loadingDiv)) {
        chatMessages.current.removeChild(loadingDiv);
      }
      
      if (!response.ok) {
        throw new Error('Network response was not ok');
      }
      
      const data = await response.json();
      
      if (data.error) {
        addMessage(`Error: ${data.error}`, false);
      } else {
        addMessage(data.answer, false);
        if (data.sources) {
          displaySources(data.sources);
        }
      }
    } catch (error) {
      // Remove loading message if still there
      if (chatMessages.current?.contains(loadingDiv)) {
        chatMessages.current.removeChild(loadingDiv);
      }
      
      addMessage(`Error: ${error instanceof Error ? error.message : 'Unknown error'}`, false);
      console.error('Error:', error);
    }
  }

  // Helper function to add a message to the chat
  const addMessage = (content: string, isUser: boolean) => {
    const newMessage: ChatMessage = {
      id: Date.now().toString(),
      role: isUser ? 'user' : 'assistant',
      content,
      timestamp: new Date().toISOString()
    };
    
    setMessages(prev => [...prev, newMessage]);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!input.trim()) return;

    // Add user message
    const userMessage: ChatMessage = {
      id: Date.now().toString(),
      role: 'user',
      content: input,
      timestamp: new Date().toISOString()
    };
    
    setMessages(prev => [...prev, userMessage]);
    const messageText = input;
    setInput('');
    setLoading(true);

    try {
      // Use the API service
      const data = await sendChatMessage(messageText);
      
      // Add assistant response
      const assistantMessage: ChatMessage = {
        id: Date.now().toString(),
        role: 'assistant',
        content: data.answer || 'Sorry, I could not process your request.',
        timestamp: new Date().toISOString()
      };
      
      setMessages(prev => [...prev, assistantMessage]);
      
      // Display sources if available
      if (data.sources) {
        displaySources(data.sources);
      }
    } catch (error) {
      console.error('Error sending message:', error);
      
      // Add error message
      const errorMessage: ChatMessage = {
        id: Date.now().toString(),
        role: 'assistant',
        content: `Error: ${error instanceof Error ? error.message : 'Failed to get response'}`,
        timestamp: new Date().toISOString()
      };
      
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setLoading(false);
    }
  };

  const handleSampleQuestion = (question: string) => {
    setInput(question);
  };

  const handleClearChat = async () => {
    try {
      // Clear backend conversation history
      await clearChatHistory();
      
      // Clear frontend state
      setMessages([
        {
          id: '1',
          role: 'assistant',
          content: 'Hello! I\'m OpenAna, your data assistant. You can ask me questions about your data from Salesforce, Affinity, or your data warehouse. How can I help you today?',
          timestamp: new Date().toISOString()
        }
      ]);
      // Clear sources as well
      setSources([]);
    } catch (error) {
      console.error('Error clearing chat history:', error);
      // Still clear frontend even if backend fails
      setMessages([
        {
          id: '1',
          role: 'assistant',
          content: 'Hello! I\'m OpenAna, your data assistant. You can ask me questions about your data from Salesforce, Affinity, or your data warehouse. How can I help you today?',
          timestamp: new Date().toISOString()
        }
      ]);
      setSources([]);
    }
  };

  // Simple mock response generator
  const generateResponse = (question: string): string => {
    if (question.toLowerCase().includes('revenue') || question.toLowerCase().includes('account')) {
      return "Based on your SFDC data, the top 5 accounts by revenue are:\n\n1. Acme Corp - $1.2M\n2. Globex Industries - $950K\n3. Initech - $820K\n4. Massive Dynamic - $750K\n5. Stark Industries - $680K\n\nWould you like to see more details about any of these accounts?";
    } else if (question.toLowerCase().includes('quality') || question.toLowerCase().includes('compare')) {
      return "I've analyzed the contact data quality between Affinity and SFDC:\n\n- Email completeness: SFDC 94%, Affinity 87%\n- Phone number validity: SFDC 76%, Affinity 82%\n- Address completeness: SFDC 65%, Affinity 42%\n- Job title accuracy: SFDC 88%, Affinity 91%\n\nOverall, SFDC has better data completeness, while Affinity has better data accuracy for the fields that are populated.";
    } else if (question.toLowerCase().includes('interaction') || question.toLowerCase().includes('xyz')) {
      return "In the last 30 days, there have been 7 interactions with company XYZ:\n\n- 3 email exchanges with their CTO about technical requirements\n- 2 phone calls with their procurement team\n- 1 in-person meeting with their CEO\n- 1 demo session with their engineering team\n\nThe most recent interaction was 2 days ago.";
    } else if (question.toLowerCase().includes('inconsistenc') || question.toLowerCase().includes('between')) {
      return "I've identified several data inconsistencies between SFDC and Affinity:\n\n1. 24 contacts have different phone numbers\n2. 18 contacts have different email addresses\n3. 12 companies have different industry classifications\n4. 8 opportunities have different status values\n\nWould you like me to generate a detailed report of these inconsistencies?";
    } else if (question.toLowerCase().includes('model') || question.toLowerCase().includes('difference')) {
      return "Key differences in data models between systems:\n\n1. Contact entity: SFDC uses separate FirstName/LastName fields, while Affinity uses a single Name field\n2. Company/Account: SFDC has more financial fields, Affinity has more relationship-oriented fields\n3. Interactions: Affinity has a richer interaction model with more context and outcome tracking\n4. Opportunities: SFDC has a more structured stage-based approach\n\nThe unified data model I recommend would preserve the granularity of SFDC's contact information while incorporating Affinity's rich interaction context.";
    } else {
      return "I've analyzed your question about " + question.split(' ').slice(0, 3).join(' ') + "... To provide a comprehensive answer, I'd need to query multiple data sources. Based on the available data, I can tell you that this information exists across your systems but may have some inconsistencies. Would you like me to generate a detailed report on this topic?";
    }
  };

  return (
    <div>
      <Typography variant="h4" gutterBottom>
        Data Q&A
      </Typography>
      
      <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 3 }}>
        <Box sx={{ flex: 3, width: '100%' }}>
          <Paper 
            sx={{ 
              height: 'calc(100vh - 200px)', 
              display: 'flex', 
              flexDirection: 'column',
              maxHeight: '70vh'
            }}
            elevation={3}
          >
            {/* Chat messages */}
            <Box 
              ref={chatMessages}
              sx={{ 
                flex: 1, 
                overflowY: 'auto', 
                p: 2,
                backgroundColor: 'grey.50'
              }}
            >
              <List>
                {messages.map((message) => (
                  <ListItem 
                    key={message.id} 
                    sx={{ 
                      display: 'flex', 
                      flexDirection: 'column', 
                      alignItems: message.role === 'user' ? 'flex-end' : 'flex-start',
                      mb: 2
                    }}
                  >
                    <Box 
                      sx={{ 
                        display: 'flex', 
                        flexDirection: 'row',
                        alignItems: 'flex-start',
                        maxWidth: '80%'
                      }}
                    >
                      {message.role === 'assistant' && (
                        <Avatar 
                          sx={{ 
                            bgcolor: 'primary.main',
                            mr: 1
                          }}
                        >
                          <BotIcon />
                        </Avatar>
                      )}
                      
                      <Paper 
                        sx={{ 
                          p: 2, 
                          bgcolor: message.role === 'user' ? 'primary.light' : 'white',
                          color: message.role === 'user' ? 'white' : 'text.primary',
                          borderRadius: 2,
                          boxShadow: 1
                        }}
                      >
                        <Typography 
                          variant="body1"
                          sx={{ whiteSpace: 'pre-line' }}
                        >
                          {message.content}
                        </Typography>
                      </Paper>
                      
                      {message.role === 'user' && (
                        <Avatar 
                          sx={{ 
                            bgcolor: 'secondary.main',
                            ml: 1
                          }}
                        >
                          <PersonIcon />
                        </Avatar>
                      )}
                    </Box>
                    
                    <Typography 
                      variant="caption" 
                      color="text.secondary"
                      sx={{ 
                        mt: 0.5,
                        alignSelf: message.role === 'user' ? 'flex-end' : 'flex-start'
                      }}
                    >
                      {new Date(message.timestamp).toLocaleTimeString()}
                    </Typography>
                  </ListItem>
                ))}
                {loading && (
                  <ListItem>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <Avatar sx={{ bgcolor: 'primary.main', mr: 1 }}>
                        <BotIcon />
                      </Avatar>
                      <CircularProgress size={24} />
                    </Box>
                  </ListItem>
                )}
                <div ref={messagesEndRef} />
              </List>
            </Box>
            
            <Divider />
            
            {/* Input area */}
            <Box sx={{ p: 2, backgroundColor: 'background.paper' }}>
              <form onSubmit={handleSubmit}>
                <Box sx={{ display: 'flex' }}>
                  <TextField
                    fullWidth
                    placeholder="Ask a question about your data..."
                    value={input}
                    onChange={handleInputChange}
                    variant="outlined"
                    disabled={loading}
                    sx={{ mr: 1 }}
                  />
                  <Button
                    type="submit"
                    variant="contained"
                    color="primary"
                    disabled={!input.trim() || loading}
                    endIcon={<SendIcon />}
                  >
                    Send
                  </Button>
                </Box>
              </form>
            </Box>
          </Paper>
          
          <Box sx={{ mt: 1 }}>
            {sources.length > 0 && (
              <Paper sx={{ p: 2, mb: 2 }}>
                <Typography variant="subtitle1" gutterBottom>
                  Sources
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {sources.map((source, index) => (
                    <Chip
                      key={index}
                      icon={<LinkIcon />}
                      label={`${source.table} (${source.source})`}
                      variant="outlined"
                      color="primary"
                      sx={{ mb: 1 }}
                      deleteIcon={
                        <IconButton size="small">
                          <InfoIcon fontSize="small" />
                        </IconButton>
                      }
                      onDelete={() => {
                        setSelectedSource(source);
                        setSourceDialogOpen(true);
                      }}
                    />
                  ))}
                </Box>
              </Paper>
            )}
              
              {/* Source Details Dialog */}
              <Dialog 
                open={sourceDialogOpen} 
                onClose={() => setSourceDialogOpen(false)}
                maxWidth="md"
                fullWidth
              >
                {selectedSource && (
                  <>
                    <DialogTitle>
                      Source: {selectedSource.table} ({selectedSource.source})
                      <IconButton
                        aria-label="close"
                        onClick={() => setSourceDialogOpen(false)}
                        sx={{
                          position: 'absolute',
                          right: 8,
                          top: 8,
                          color: (theme) => theme.palette.grey[500],
                        }}
                      >
                        <CloseIcon />
                      </IconButton>
                    </DialogTitle>
                    <DialogContent dividers>
                      <Typography variant="subtitle2" gutterBottom>Table Information:</Typography>
                      <Paper variant="outlined" sx={{ p: 1.5, mb: 2, bgcolor: 'grey.50' }}>
                        <Typography variant="body2">
                          <strong>Table:</strong> {selectedSource.table}<br/>
                          <strong>Source:</strong> {selectedSource.source}<br/>
                          <strong>Rows Used:</strong> {selectedSource.rows_used}<br/>
                          <strong>Total Rows:</strong> {selectedSource.total_rows}<br/>
                          <strong>Columns:</strong> {selectedSource.columns.join(', ')}
                        </Typography>
                      </Paper>
                      
                      <Typography variant="subtitle2" gutterBottom>Available Columns:</Typography>
                      <Paper variant="outlined" sx={{ p: 1.5, bgcolor: 'grey.50' }}>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                          {selectedSource.columns.map((column, index) => (
                            <Chip key={index} label={column} size="small" variant="outlined" />
                          ))}
                        </Box>
                      </Paper>
                    </DialogContent>
                    <DialogActions>
                      <Button onClick={() => setSourceDialogOpen(false)}>Close</Button>
                    </DialogActions>
                  </>
                )}
              </Dialog>
            
            <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
              <Button
                startIcon={<DeleteIcon />}
                onClick={handleClearChat}
                color="secondary"
                variant="outlined"
                size="small"
              >
                Clear Chat
              </Button>
            </Box>
          </Box>
        </Box>
        
        <Box sx={{ flex: 1, width: '100%' }}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Sample Questions
              </Typography>
              <Typography variant="body2" color="text.secondary" paragraph>
                Try asking OpenAna these questions about your data:
              </Typography>
              <List dense>
                {sampleQuestions.map((question, index) => (
                  <ListItem key={index} disablePadding>
                    <Button
                      fullWidth
                      onClick={() => handleSampleQuestion(question)}
                      sx={{ 
                        justifyContent: 'flex-start', 
                        textAlign: 'left',
                        py: 1
                      }}
                    >
                      {question}
                    </Button>
                  </ListItem>
                ))}
              </List>
              <Divider sx={{ my: 2 }} />
              <Typography variant="body2" color="text.secondary">
                OpenAna can analyze data across all your systems to provide insights and answer complex questions.
              </Typography>
            </CardContent>
          </Card>
        </Box>
      </Box>
    </div>
  );
};

export default DataQA;
